// Piezas comunes de las pantallas de Compras: el nombre del proveedor por id, el diálogo que pide el
// motivo de una firma (aprobar, rechazar, cancelar, reversar…) y la historia de transiciones.
import { useState, type ReactNode } from 'react';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { entradaDecimal, fechaHora } from '../comun/formato';
import { useNombreEntidad, type Transicion } from './datos';

export function NombreProveedor({ id }: { id: string | null | undefined }) {
  const q = useNombreEntidad(id);
  if (!id) return <span className="apagado">—</span>;
  return <>{q.data?.razon_social ?? '…'}</>;
}

/**
 * Pide el motivo (o el texto que la acción necesite) y confirma. El mínimo lo pone la API (3 a 10
 * caracteres según el acto): aquí se exige el mismo para no mandar algo que se va a rechazar.
 */
export function DialogoMotivo({
  titulo,
  etiqueta = 'Motivo',
  minimo = 3,
  textoBoton,
  explicacion,
  pendiente,
  error,
  alConfirmar,
  alCerrar,
}: {
  titulo: string;
  etiqueta?: string;
  minimo?: number;
  textoBoton: string;
  explicacion?: ReactNode;
  pendiente: boolean;
  error: unknown;
  alConfirmar: (texto: string) => void;
  alCerrar: () => void;
}) {
  const [texto, setTexto] = useState('');
  const valido = texto.trim().length >= minimo;
  return (
    <Modal titulo={titulo} alCerrar={alCerrar} ancho="angosto">
      {explicacion && <p>{explicacion}</p>}
      <label className="campo">
        <span className="etiqueta">
          {etiqueta} {minimo > 3 && <span className="apagado">(mínimo {minimo} caracteres)</span>}
        </span>
        <textarea
          aria-label={etiqueta}
          autoFocus
          rows={3}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
      </label>
      {error ? <PanelError error={error} /> : null}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={!valido || pendiente}
          onClick={() => alConfirmar(texto.trim())}
        >
          {textoBoton}
        </button>
      </div>
    </Modal>
  );
}

const ACCION: Record<string, string> = {
  crear: 'Creada',
  editar: 'Editada',
  enviar_aprobacion: 'Enviada a aprobación',
  aprobar_por_politica: 'Aprobada por política',
  retirar_aprobacion: 'Retirada de aprobación',
  aprobar: 'Aprobada',
  rechazar: 'Rechazada',
  reabrir: 'Reabierta',
  emitir: 'Emitida',
  cancelar: 'Cancelada',
  cerrar: 'Cerrada',
  cerrar_corta: 'Cerrada corta',
  confirmar: 'Confirmada',
  descartar: 'Descartada',
  reversar: 'Reversada',
  registrar: 'Registrada',
  conciliar: 'Conciliada',
  aceptar: 'Aceptada',
  solicitar: 'Solicitada',
  imputar: 'Imputada',
  medir: 'Conciliación medida',
  resolver: 'Diferencia resuelta',
};

/** «ABIERTA» → «Abierta», «CON_DIFERENCIAS» → «Con diferencias» (la historia se lee como texto). */
const legible = (e: string) => e.charAt(0) + e.slice(1).toLowerCase().replace(/_/g, ' ');

/** La historia del documento: quién hizo qué y cuándo (append-only en el backend). */
export function Historia({ transiciones }: { transiciones: Transicion[] }) {
  if (!transiciones.length) return <p className="apagado">Sin movimientos.</p>;
  return (
    <table className="grilla compacta" aria-label="Historia del documento">
      <thead>
        <tr>
          <th style={{ width: '10rem' }}>Fecha</th>
          <th>Acción</th>
          <th>Estado</th>
          <th>Motivo</th>
        </tr>
      </thead>
      <tbody>
        {transiciones.map((t) => (
          <tr key={t.id}>
            <td>{fechaHora(t.en)}</td>
            <td>{ACCION[t.accion] ?? t.accion}</td>
            <td className="apagado">
              {t.estado_desde && t.estado_desde !== t.estado_hasta
                ? `${legible(t.estado_desde)} → `
                : ''}
              {t.estado_hasta ? legible(t.estado_hasta) : ''}
            </td>
            <td>{t.motivo ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Texto decimal digitado → número solo para MOSTRAR una estimación (nunca se envía). */
export function estimar(texto: string): number {
  const t = texto.trim().replace(/\s/g, '');
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return Number.isFinite(n) ? n : 0;
}

export const hoy = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Santiago' });

/**
 * Unidades base para la API (hasta 4 decimales, como el ledger): acepta lo que vino del backend
 * ("100.000000") o lo digitado ("1.234,5"). Solo recorta ceros sobrantes: si hay más de 4 decimales
 * significativos, lo deja tal cual y la API lo rechaza con su mensaje (nunca se redondea en silencio).
 */
export function cuatroDecimales(v: string): string {
  const t = v.trim();
  const crudo = /^\d+(\.\d+)?$/.test(t) ? t : (entradaDecimal(t, 6) ?? t);
  const [e, f = ''] = crudo.split('.');
  const frac = f.replace(/0+$/, '');
  return frac ? `${e}.${frac}` : (e ?? crudo);
}
